/* eslint-disable no-nested-ternary */
import React, { ReactNode } from 'react';
import { classNames } from 'primereact/utils';
import { Link } from 'react-router-dom';
import { Icon } from 'components/_new/Icon';
import './ProgressSteps.scss';

export type ProgressStepItem = {
	id: string;
	label: ReactNode;
	href?: string;
	hash?: string;
	disabled?: boolean;
};

export type ProgressStepsProps = {
	title: ReactNode;
	variant: 'mentee' | 'mentor' | 'mentee-white' | 'mentor-white';
	steps: ProgressStepItem[];
	activeStep: number;
	action?: { label: string; to: string };
	onStepSelect?: (step: ProgressStepItem, index: number) => void;
};

const getNormalizedActiveStep = (stepsLength: number, activeStep: number) => {
	if (stepsLength <= 0) {
		return 0;
	}

	if (activeStep < 0) {
		return 0;
	}

	if (activeStep >= stepsLength) {
		return stepsLength - 1;
	}

	return activeStep;
};

const getStepState = (stepIndex: number, activeStep: number) => {
	if (stepIndex < activeStep) {
		return 'completed';
	}

	if (stepIndex === activeStep) {
		return 'current';
	}

	return 'upcoming';
};

const ProgressSteps = ({ title, variant, steps, activeStep, action, onStepSelect }: ProgressStepsProps) => {
	if (!steps || steps.length === 0) {
		return null;
	}

	const normalizedActiveStep = getNormalizedActiveStep(steps.length, activeStep);
	const hasManySteps = steps.length >= 9;

	return (
		<div
			className={classNames(
				'progress-steps-card progress-steps-card--desktop-only',
				`progress-steps-card--${variant}`,
				{
					'progress-steps-card--dense': hasManySteps,
				}
			)}
		>
			<div className="progress-steps-card__header">
				<div className="progress-steps-card__title">{title}</div>
				{action ? (
					<Link to={{ pathname: action.to }} className="progress-steps-card__action text-md-minus">
						<span>{action.label}</span>
						<Icon name="arrow-right" className="text-sm" />
					</Link>
				) : null}
			</div>

			<div className="progress-steps-card__timeline">
				<ol className="progress-steps-card__list">
					{steps.map((step, index) => {
						const state = getStepState(index, normalizedActiveStep);
						const isClickable = !step.disabled && (Boolean(step.href) || Boolean(onStepSelect));
						const content = (
							<>
								<span className="progress-steps-card__marker" aria-hidden>
									{state === 'completed' ? (
										<Icon name="check" className="progress-steps-card__check" />
									) : null}
								</span>
								<span className="progress-steps-card__label px-1">{step.label}</span>
							</>
						);

						return (
							<li
								key={step.id}
								data-state={state}
								className={classNames('progress-steps-card__item', `progress-step--${state}`, {
									'progress-step--clickable': isClickable,
									'progress-step--disabled': step.disabled,
								})}
							>
								{step.href && !onStepSelect && !step.disabled ? (
									<Link
										to={{ pathname: step.href, hash: step.hash }}
										className="progress-steps-card__step"
									>
										{content}
									</Link>
								) : isClickable ? (
									<button
										type="button"
										className="progress-steps-card__step"
										onClick={() => onStepSelect?.(step, index)}
									>
										{content}
									</button>
								) : (
									<div className="progress-steps-card__step">{content}</div>
								)}
							</li>
						);
					})}
				</ol>
			</div>
		</div>
	);
};

export default ProgressSteps;
